import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { Trash } from 'react-bootstrap-icons';
import AuthenticationEndpoint from '../../../api/endpoint/AuthenticationEndpoint';
import { sharedSearchCache } from '../../../api/SearchCache';
import { getData } from '../../utils/GetData';
import DataTable, { DataTableColumn } from '../widget/DataTable';

type ManagedUser = {
    [key: string]: unknown;
    user_id: number;
    username: string;
    user_role: string;
};

export const UserManagement = () => {
    const [users, setUsers] = useState<ManagedUser[] | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: 'asc' | 'desc' | 'none' }>({
        column: null,
        direction: 'asc'
    });
    const [searchError, setSearchError] = useState<string | null>(null);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<ManagedUser | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pendingReset, setPendingReset] = useState<ManagedUser | null>(null);
    const [resetPassword, setResetPassword] = useState('');
    const [resetConfirmation, setResetConfirmation] = useState('');
    const [isResetting, setIsResetting] = useState(false);

    const reload = useCallback(() => {
        sharedSearchCache.clear();
        setUsers(undefined);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadUsers = async () => {
            const result = await getData<ManagedUser>('/api/auth/users/search', page, pageSize, {
                column: sort.column,
                direction: sort.direction
            });

            if (cancelled) return;
            if (typeof result === 'string') {
                setSearchError(result);
                setUsers([]);
                return;
            }

            setSearchError(null);
            setUsers(result.data);
            setTotal(result.total);
        };

        loadUsers();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize, sort]);

    const handleSort = (column: DataTableColumn<ManagedUser>) => {
        if (!column.sortable) return;

        let direction: 'asc' | 'desc' | 'none' = 'asc';
        if (sort.column === column.column) {
            direction = sort.direction === 'asc' ? 'desc' : sort.direction === 'desc' ? 'none' : 'asc';
        }

        setSort({
            column: direction === 'none' ? null : column.column,
            direction
        });
        setPage(1);
    };


    const deleteUser = async () => {
        if (!pendingDelete) return;

        setIsDeleting(true);
        setOperationMessage(null);
        try {
            const result = await AuthenticationEndpoint.DeleteUser(pendingDelete.user_id);
            if (result.errors.length > 0) {
                throw new Error(result.errors.map((error) => error.message).join('; '));
            }
            setPendingDelete(null);
            setOperationMessage(`Deleted ${pendingDelete.username}.`);
            if (users?.length === 1 && page > 1) setPage(page - 1);
            reload();
        } catch (error) {
            setOperationMessage(error instanceof Error ? error.message : 'Unable to delete the user.');
        } finally {
            setIsDeleting(false);
        }
    };

    const closeResetModal = () => {
        setPendingReset(null);
        setResetPassword('');
        setResetConfirmation('');
    };

    const resetUserPassword = async () => {
        if (!pendingReset || resetPassword !== resetConfirmation) return;

        setIsResetting(true);
        setOperationMessage(null);
        try {
            const result = await AuthenticationEndpoint.ResetUserPassword(pendingReset.user_id, resetPassword);
            if (result.errors.length > 0) {
                throw new Error(result.errors.map((error) => error.message).join('; '));
            }
            setOperationMessage(`Password reset for ${pendingReset.username}.`);
            closeResetModal();
        } catch (error) {
            setOperationMessage(error instanceof Error ? error.message : 'Unable to reset the password.');
        } finally {
            setIsResetting(false);
        }
    };

    const columns = useMemo<DataTableColumn<ManagedUser>[]>(() => [
        { column: 'user_id', label: 'ID', sortable: true, cellClassName: 'align-middle' },
        { column: 'username', label: 'Username', sortable: true, cellClassName: 'align-middle' },
        {
            column: 'reset-password',
            label: 'Password',
            cellClassName: 'align-middle',
            accessor: (user) => (
                <Button
                    variant="secondary"
                    onClick={() => setPendingReset(user)}
                    aria-label={`Reset password for ${user.username}`}
                >
                    Reset
                </Button>
            )
        },
        {
            column: 'delete',
            label: 'Delete',
            cellClassName: 'align-middle',
            accessor: (user) => (
                <Button
                    variant="danger"
                    className="text-light"
                    aria-label={`Delete ${user.username}`}
                    onClick={() => setPendingDelete(user)}
                >
                    <Trash aria-hidden="true" />
                </Button>
            )
        }
    ], [reload, users]);

    return (
        <div>
            <h1 className="text-light">User Management</h1>
            <h2 className="text-light" style={{ fontSize: '1rem' }}>
                Manage administrator and instructor accounts.
            </h2>
            <hr className="border" />

            <div style={{ padding: '1em', backgroundColor: 'white', borderRadius: '0.5em' }}>
                {operationMessage && <div className="mb-3" role="status">{operationMessage}</div>}
                {searchError && (
                    <div className="text-danger" role="alert">
                        <strong>Search error:</strong> {searchError}
                    </div>
                )}
                {users === undefined && <p role="status">Loading users...</p>}
                {users !== undefined && (
                    <DataTable
                        tableClassName="table-stack-xs"
                        data={users}
                        columns={columns}
                        id="user-management-list"
                        page={page}
                        pageSize={pageSize}
                        totalRecords={total}
                        onPageChange={setPage}
                        sort={sort}
                        onSort={handleSort}
                        handlePageSizeChange={(newPageSize) => {
                            setPageSize(newPageSize);
                            setPage(1);
                        }}
                    />
                )}
            </div>

            <Modal show={pendingDelete !== null} onHide={() => !isDeleting && setPendingDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete user?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This permanently deletes {pendingDelete?.username}'s account. This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPendingDelete(null)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" className="text-light" onClick={deleteUser} disabled={isDeleting}>
                        {isDeleting ? 'Deleting...' : 'Delete user'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={pendingReset !== null} onHide={() => !isResetting && closeResetModal()} centered>
                <Modal.Header closeButton={!isResetting}>
                    <Modal.Title>Reset password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Set a new password for {pendingReset?.username}.</p>
                    <Form.Group className="mb-3" controlId="user-reset-password">
                        <Form.Label>New password</Form.Label>
                        <Form.Control
                            type="password"
                            value={resetPassword}
                            onChange={(event) => setResetPassword(event.target.value)}
                            autoComplete="new-password"
                        />
                    </Form.Group>
                    <Form.Group controlId="user-reset-password-confirmation">
                        <Form.Label>Confirm new password</Form.Label>
                        <Form.Control
                            type="password"
                            value={resetConfirmation}
                            onChange={(event) => setResetConfirmation(event.target.value)}
                            isInvalid={resetConfirmation.length > 0 && resetPassword !== resetConfirmation}
                            autoComplete="new-password"
                        />
                        <Form.Control.Feedback type="invalid">Passwords must match.</Form.Control.Feedback>
                    </Form.Group>
                    <small className="text-body-secondary">
                        Use at least 8 characters with uppercase, lowercase, and a number.
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeResetModal} disabled={isResetting}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="text-light"
                        onClick={resetUserPassword}
                        disabled={isResetting || !resetPassword || resetPassword !== resetConfirmation}
                    >
                        {isResetting ? 'Resetting...' : 'Reset password'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
